import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Users } from "lucide-react";

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const AdminCustomers = () => {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (status !== "all") params.set("status", status);
      params.set("skip", String(skip));
      params.set("limit", String(limit));
      const { data } = await api.get(`/admin/customers?${params}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load customers", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [debouncedQ, status, skip, limit]);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (uid, active) => {
    if (!window.confirm(active ? "Reactivate this customer?" : "Suspend this customer?")) return;
    try {
      await api.patch(`/admin/customers/${uid}/status`, { is_active: active });
      toast.success(active ? "Customer reactivated" : "Customer suspended");
      load();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
  };

  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout title="Customers">
      <div className="border border-border bg-card p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customer-search" placeholder="Search by name or email…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="rounded-none h-11 pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
          <SelectTrigger data-testid="customer-status-filter" className="rounded-none h-11 w-full md:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card">
        <div className="hidden md:grid grid-cols-[1fr_140px_100px_120px_100px_130px_120px] gap-3 items-center px-4 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
          <div>Customer</div><div>Registered</div><div>Purchases</div><div>Spent</div><div>Wishlist</div><div>Status</div><div></div>
        </div>
        {loading && <div className="p-8 text-center text-muted-foreground text-sm" data-testid="customers-loading">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center" data-testid="customers-empty">
            <Users className="mx-auto mb-3 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">No customers found.</div>
          </div>
        )}
        {!loading && items.map((c) => (
          <div key={c.user_id} data-testid={`customer-row-${c.user_id}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_100px_120px_100px_130px_120px] gap-3 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-background/60">
            <div className="min-w-0">
              <Link to={`/admin/customers/${c.user_id}`} className="text-sm font-medium hover:text-accent block truncate" data-testid={`customer-link-${c.user_id}`}>
                {c.name}
              </Link>
              <div className="text-[11px] text-muted-foreground truncate">{c.email} {c.email_verified ? " · ✓ verified" : " · unverified"}</div>
            </div>
            <div className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</div>
            <div className="text-sm">{c.purchase_count}</div>
            <div className="text-sm">{fmtUSD(c.total_spend)}</div>
            <div className="text-sm">{c.wishlist_count}</div>
            <div>
              <span data-testid={`customer-status-${c.user_id}`} className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${c.is_active !== false ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-red-700 border-red-200 bg-red-50"}`}>
                {c.is_active !== false ? "Active" : "Suspended"}
              </span>
            </div>
            <div className="justify-self-end">
              <Button
                data-testid={`toggle-status-${c.user_id}`}
                variant="outline"
                size="sm"
                onClick={() => toggleStatus(c.user_id, c.is_active === false)}
                className="rounded-none text-xs"
              >
                {c.is_active === false ? "Reactivate" : "Suspend"}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {total > limit && (
        <div className="flex justify-between items-center mt-4 text-sm" data-testid="customers-pagination">
          <div className="text-muted-foreground">Page {page} of {pages} · {total} total</div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="cust-prev">Previous</Button>
            <Button variant="outline" className="rounded-none" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="cust-next">Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCustomers;
